import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';
import { getAllEnabledModules, ModuleFlags, normalizeModuleFlags } from '../utils/moduleAccess';
import { getTenantBranding, TenantBranding } from '../utils/tenantBranding';

interface Tenant {
  id: number;
  name: string;
  subdomain: string;
  domain?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  status: 'active' | 'trial' | 'expired' | 'suspended';
  subscription_tier?: string;
  is_subscription_exempt?: number | boolean;
  max_students?: number;
  max_teachers?: number;
  billing_email?: string;
  trial_ends_at?: string;
  subscription_renews_at?: string;
  feature_flags?: Partial<ModuleFlags>;
  enabled_modules?: string[];
  plan_package?: string;
}

interface TenantContextType {
  tenant: Tenant | null;
  branding: TenantBranding;
  moduleFlags: ModuleFlags;
  loading: boolean;
  error: string | null;
  refreshTenant: () => Promise<void>;
  hasModule: (moduleKey: keyof ModuleFlags | string) => boolean;
}

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  branding: getTenantBranding(null),
  moduleFlags: getAllEnabledModules(),
  loading: true,
  error: null,
  refreshTenant: async () => { },
  hasModule: () => true
});

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [moduleFlags, setModuleFlags] = useState<ModuleFlags>(getAllEnabledModules());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const branding = getTenantBranding(tenant);

  const fetchTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const role = localStorage.getItem('role');
      // For non-authenticated users (public pages), use subdomain-based lookup
      // For authenticated users, use /tenant/current which gets tenant from JWT
      let url: string;

      if (token && role && role !== 'guest') {
        // Authenticated user - get tenant from JWT token
        url = `${API_BASE_URL}/api/tenant/current`;
      } else {
        // Public page - try to get tenant from subdomain
        const hostname = window.location.hostname;
        const subdomain = hostname.split('.')[0];
        const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
        const isIpHost = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
        if (isLocalhost || isIpHost) {
          setTenant(null);
          setModuleFlags(getAllEnabledModules());
          applyTenantBranding(null);
          return;
        }
        const tenantIdentifier = subdomain;
        url = `${API_BASE_URL}/api/tenant/info?subdomain=${encodeURIComponent(tenantIdentifier)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token || ''}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const tenantData = await response.json();
        setTenant(tenantData);
        setModuleFlags(normalizeModuleFlags(tenantData?.feature_flags));

        applyTenantBranding(tenantData);
      } else if (response.status === 404) {
        setTenant(null);
        setModuleFlags(getAllEnabledModules());
        if (token && role === 'superadmin') {
          setError(null);
        } else {
          setError('School not found');
        }
        applyTenantBranding(null);
      } else {
        throw new Error(`Failed to fetch tenant: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Error fetching tenant:', err);
      // Don't set error for auth users - try to use school logo endpoint as fallback
      if (!localStorage.getItem('token')) {
        setError(err.message || 'Failed to load tenant information');
        setTenant(null);
        setModuleFlags(getAllEnabledModules());
      }
      applyTenantBranding(null);
    } finally {
      setLoading(false);
    }
  }, []);


  const applyTenantBranding = (tenantData: Tenant | null) => {
    const nextBranding = getTenantBranding(tenantData);
    const root = document.documentElement;

    root.style.setProperty('--tenant-primary-color', nextBranding.primaryColor);
    root.style.setProperty('--tenant-secondary-color', nextBranding.secondaryColor);
    root.style.setProperty('--brand-primary', nextBranding.primaryColor);
    root.style.setProperty('--brand-primary-contrast', nextBranding.primaryContrast);
    root.style.setProperty('--brand-primary-soft', nextBranding.primarySoft);
    root.style.setProperty('--brand-secondary', nextBranding.secondaryColor);
    root.style.setProperty('--brand-secondary-soft', nextBranding.secondarySoft);

    document.title = nextBranding.portalTitle;

    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (favicon) {
      favicon.href = nextBranding.faviconUrl;
    }

    const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
    if (appleTouchIcon) {
      appleTouchIcon.href = nextBranding.faviconUrl;
    }
  };

  const refreshTenant = async () => {
    await fetchTenant();
  };

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  // Listen for subdomain changes (in case user navigates between tenants)
  useEffect(() => {
    const handleLocationChange = () => {
      const newHostname = window.location.hostname;
      const newSubdomain = newHostname.split('.')[0];

      if (tenant && tenant.subdomain !== newSubdomain) {
        fetchTenant();
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, [tenant, fetchTenant]);

  return (
    <TenantContext.Provider value={{
      tenant,
      branding,
      moduleFlags,
      loading,
      error,
      refreshTenant,
      hasModule: (moduleKey) => {
        if (!moduleKey) return true;
        return Boolean(moduleFlags[moduleKey as keyof ModuleFlags]);
      }
    }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// Hook for checking subscription status
export const useSubscriptionStatus = () => {
  const { tenant } = useTenant();

  if (!tenant) {
    return { isActive: false, status: 'unknown', message: 'No tenant information available' };
  }

  const now = new Date();
  const trialEndsAt = tenant.trial_ends_at ? new Date(tenant.trial_ends_at) : null;
  const subscriptionRenewsAt = tenant.subscription_renews_at ? new Date(tenant.subscription_renews_at) : null;

  switch (tenant.status) {
    case 'active':
      return {
        isActive: true,
        status: 'active',
        message: subscriptionRenewsAt ? `Active until ${subscriptionRenewsAt.toLocaleDateString()}` : 'Active subscription'
      };

    case 'trial':
      if (trialEndsAt && trialEndsAt > now) {
        const daysLeft = Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return {
          isActive: true,
          status: 'trial',
          message: `Trial ends in ${daysLeft} days`
        };
      } else {
        return {
          isActive: false,
          status: 'trial_expired',
          message: 'Trial period has ended'
        };
      }

    case 'expired':
      return {
        isActive: false,
        status: 'expired',
        message: 'Subscription has expired'
      };

    case 'suspended':
      return {
        isActive: false,
        status: 'suspended',
        message: 'Account has been suspended'
      };

    default:
      return {
        isActive: false,
        status: 'unknown',
        message: 'Unknown subscription status'
      };
  }
};
